/**
 * MarketplaceFilters Component
 * Filter sidebar/panel for marketplace
 * BP-08-04: Marketplace Frontend (Family)
 */

import React, { useState, useMemo } from 'react';
import { 
  ChevronDown, 
  ChevronUp, 
  X, 
  Filter,
  Shield,
  Star 
} from 'lucide-react';
import { StarRating } from './StarRating';
import type { MarketplaceFiltersProps, MarketplaceFilters as FiltersType, VendorCategory } from '@/features/marketplace/types/marketplaceTypes';

interface FilterSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const FilterSection: React.FC<FilterSectionProps> = ({
  title,
  children,
  defaultOpen = true,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border-b border-warm-gray/10 py-4 last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="
          w-full flex items-center justify-between
          text-navy font-semibold
          hover:text-sage-dark transition-colors
        "
      >
        {title}
        {isOpen ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
      </button>
      
      {isOpen && (
        <div className="mt-3">
          {children}
        </div>
      )}
    </div>
  );
};

const SORT_OPTIONS = [
  { value: 'relevance', label: 'Most Relevant' },
  { value: 'price_low', label: 'Price: Low to High' },
  { value: 'price_high', label: 'Price: High to Low' },
  { value: 'rating', label: 'Highest Rated' },
  { value: 'newest', label: 'Newest First' },
  { value: 'popular', label: 'Most Popular' },
] as const;

const PRICE_RANGES = [
  { min: 0, max: 100, label: 'Under $100' },
  { min: 100, max: 500, label: '$100 - $500' },
  { min: 500, max: 1000, label: '$500 - $1,000' },
  { min: 1000, max: 5000, label: '$1,000 - $5,000' },
  { min: 5000, max: null, label: '$5,000+' },
];

export const MarketplaceFilters: React.FC<MarketplaceFiltersProps> = ({
  categories,
  onFilterChange,
  initialFilters = {},
  showCategoryFilter = true,
  showPriceFilter = true,
  showRatingFilter = true,
  showInsuranceFilter = true,
  orientation = 'vertical',
}) => {
  const [filters, setFilters] = useState<FiltersType>(initialFilters);
  const [customPriceMin, setCustomPriceMin] = useState('');
  const [customPriceMax, setCustomPriceMax] = useState('');
  
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.categoryId) count++;
    if (filters.priceMin !== undefined || filters.priceMax !== undefined) count++;
    if (filters.minRating) count++;
    if (filters.acceptsInsurance) count++;
    return count;
  }, [filters]);
  
  const updateFilters = (newFilters: Partial<FiltersType>) => {
    const updated = { ...filters, ...newFilters };
    setFilters(updated);
    onFilterChange(updated);
  };
  
  const clearFilters = () => {
    const cleared: FiltersType = { sortBy: filters.sortBy };
    setFilters(cleared);
    setCustomPriceMin('');
    setCustomPriceMax('');
    onFilterChange(cleared);
  };
  
  const handlePriceRangeSelect = (min: number, max: number | null) => {
    updateFilters({
      priceMin: min,
      priceMax: max ?? undefined,
    });
  };
  
  const handleCustomPriceApply = () => {
    const min = customPriceMin ? parseInt(customPriceMin, 10) : undefined;
    const max = customPriceMax ? parseInt(customPriceMax, 10) : undefined;
    updateFilters({ priceMin: min, priceMax: max });
  };
  
  const buildCategoryTree = (cats: VendorCategory[]): VendorCategory[] => {
    const rootCategories = cats.filter((c) => !c.parentId);
    return rootCategories.map((root) => ({
      ...root,
      children: cats.filter((c) => c.parentId === root.id),
    }));
  };
  
  const categoryTree = useMemo(() => buildCategoryTree(categories), [categories]);
  
  if (orientation === 'horizontal') {
    return (
      <div className="
        flex flex-wrap items-center gap-3
        p-4 bg-white rounded-xl
        border border-warm-gray/10
      ">
        <div className="flex items-center gap-2 text-navy font-medium">
          <Filter className="w-4 h-4" />
          Filters
          {activeFilterCount > 0 && (
            <span className="
              px-2 py-0.5 rounded-full
              bg-sage text-white text-xs font-semibold
            ">
              {activeFilterCount}
            </span>
          )}
        </div>
        
        {/* Sort */}
        <select
          value={filters.sortBy || 'relevance'}
          onChange={(e) => updateFilters({ sortBy: e.target.value as FiltersType['sortBy'] })}
          className="
            px-3 py-2 rounded-lg
            bg-cream border-none
            text-sm text-navy
            focus:ring-2 focus:ring-sage/20
          "
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        
        {/* Category */}
        {showCategoryFilter && categories.length > 0 && (
          <select
            value={filters.categoryId || ''}
            onChange={(e) => updateFilters({ categoryId: e.target.value || undefined })}
            className="
              px-3 py-2 rounded-lg
              bg-cream border-none
              text-sm text-navy
              focus:ring-2 focus:ring-sage/20
            "
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        )}
        
        {/* Rating */}
        {showRatingFilter && (
          <button
            onClick={() => updateFilters({ minRating: filters.minRating ? undefined : 4 })}
            className={`
              px-3 py-2 rounded-lg text-sm font-medium
              flex items-center gap-1.5
              transition-colors duration-200
              ${filters.minRating
                ? 'bg-sage text-white'
                : 'bg-cream text-navy-light hover:bg-cream-dark'
              }
            `}
          >
            <Star className="w-4 h-4" fill={filters.minRating ? 'currentColor' : 'none'} />
            4+ Stars
          </button>
        )}
        
        {/* Insurance */}
        {showInsuranceFilter && (
          <button
            onClick={() => updateFilters({ acceptsInsurance: !filters.acceptsInsurance })}
            className={`
              px-3 py-2 rounded-lg text-sm font-medium
              flex items-center gap-1.5
              transition-colors duration-200
              ${filters.acceptsInsurance
                ? 'bg-sage text-white'
                : 'bg-cream text-navy-light hover:bg-cream-dark'
              }
            `}
          >
            <Shield className="w-4 h-4" />
            Insurance
          </button>
        )}
        
        {activeFilterCount > 0 && (
          <button
            onClick={clearFilters}
            className="
              px-3 py-2 rounded-lg text-sm font-medium
              text-terracotta hover:bg-terracotta/10
              transition-colors duration-200
            "
          >
            Clear All
          </button>
        )}
      </div>
    );
  }
  
  // Vertical orientation (sidebar)
  return (
    <div className="bg-white rounded-xl border border-warm-gray/10 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 font-semibold text-navy">
          <Filter className="w-5 h-5" />
          Filters
          {activeFilterCount > 0 && (
            <span className="
              px-2 py-0.5 rounded-full
              bg-sage text-white text-xs font-semibold
            ">
              {activeFilterCount}
            </span>
          )}
        </div>
        
        {activeFilterCount > 0 && (
          <button
            onClick={clearFilters}
            className="
              text-sm text-terracotta hover:underline
              transition-colors duration-200
            "
          >
            Clear All
          </button>
        )}
      </div>
      
      {/* Sort */}
      <FilterSection title="Sort By">
        <div className="space-y-1">
          {SORT_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => updateFilters({ sortBy: option.value })}
              className={`
                w-full px-3 py-2 rounded-lg text-left text-sm
                transition-colors duration-200
                ${filters.sortBy === option.value
                  ? 'bg-sage/10 text-sage-dark font-medium'
                  : 'text-navy-light hover:bg-cream'
                }
              `}
            >
              {option.label}
            </button>
          ))}
        </div>
      </FilterSection>
      
      {/* Categories */}
      {showCategoryFilter && categories.length > 0 && (
        <FilterSection title="Category">
          <div className="space-y-1">
            <button
              onClick={() => updateFilters({ categoryId: undefined })}
              className={`
                w-full px-3 py-2 rounded-lg text-left text-sm
                transition-colors duration-200
                ${!filters.categoryId
                  ? 'bg-sage/10 text-sage-dark font-medium'
                  : 'text-navy-light hover:bg-cream'
                }
              `}
            >
              All Categories
            </button>
            
            {categoryTree.map((category) => (
              <div key={category.id}>
                <button
                  onClick={() => updateFilters({ categoryId: category.id })}
                  className={`
                    w-full px-3 py-2 rounded-lg text-left text-sm
                    transition-colors duration-200
                    ${filters.categoryId === category.id
                      ? 'bg-sage/10 text-sage-dark font-medium'
                      : 'text-navy-light hover:bg-cream'
                    }
                  `}
                >
                  {category.name}
                </button>
                
                {category.children && category.children.length > 0 && (
                  <div className="ml-4 mt-1 space-y-1">
                    {category.children.map((child) => (
                      <button
                        key={child.id}
                        onClick={() => updateFilters({ categoryId: child.id })}
                        className={`
                          w-full px-3 py-1.5 rounded-lg text-left text-sm
                          transition-colors duration-200
                          ${filters.categoryId === child.id
                            ? 'bg-sage/10 text-sage-dark font-medium'
                            : 'text-warm-gray hover:bg-cream'
                          }
                        `}
                      >
                        {child.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </FilterSection>
      )}
      
      {/* Price */}
      {showPriceFilter && (
        <FilterSection title="Price Range">
          <div className="space-y-2">
            {PRICE_RANGES.map((range, index) => {
              const isSelected = 
                filters.priceMin === range.min && 
                (range.max === null ? filters.priceMax === undefined : filters.priceMax === range.max);
              
              return (
                <button
                  key={index}
                  onClick={() => handlePriceRangeSelect(range.min, range.max)}
                  className={`
                    w-full px-3 py-2 rounded-lg text-left text-sm
                    transition-colors duration-200
                    ${isSelected
                      ? 'bg-sage/10 text-sage-dark font-medium'
                      : 'text-navy-light hover:bg-cream'
                    }
                  `}
                >
                  {range.label}
                </button>
              );
            })}
            
            {/* Custom range */}
            <div className="pt-2 space-y-2">
              <span className="text-xs text-warm-gray">Custom Range</span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={customPriceMin}
                  onChange={(e) => setCustomPriceMin(e.target.value)}
                  className="
                    w-full px-3 py-2 rounded-lg
                    bg-cream border-2 border-transparent
                    text-sm text-navy
                    focus:border-sage focus:outline-none
                  "
                />
                <span className="text-warm-gray">–</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={customPriceMax}
                  onChange={(e) => setCustomPriceMax(e.target.value)}
                  className="
                    w-full px-3 py-2 rounded-lg
                    bg-cream border-2 border-transparent
                    text-sm text-navy
                    focus:border-sage focus:outline-none
                  "
                />
              </div>
              <button
                onClick={handleCustomPriceApply}
                className="
                  w-full px-3 py-2 rounded-lg
                  bg-cream hover:bg-cream-dark
                  text-sm font-medium text-navy
                  transition-colors duration-200
                "
              >
                Apply
              </button>
            </div>
          </div>
        </FilterSection>
      )}
      
      {/* Rating */}
      {showRatingFilter && (
        <FilterSection title="Minimum Rating">
          <div className="space-y-2">
            {[4, 3, 2, 1].map((rating) => (
              <button
                key={rating}
                onClick={() => updateFilters({ 
                  minRating: filters.minRating === rating ? undefined : rating 
                })}
                className={`
                  w-full px-3 py-2 rounded-lg text-left
                  flex items-center gap-2
                  transition-colors duration-200
                  ${filters.minRating === rating
                    ? 'bg-sage/10'
                    : 'hover:bg-cream'
                  }
                `}
              >
                <StarRating rating={rating} size="sm" />
                <span className="text-sm text-navy-light">& up</span>
              </button>
            ))}
          </div>
        </FilterSection>
      )}
      
      {/* Insurance */}
      {showInsuranceFilter && (
        <FilterSection title="Insurance">
          <button
            onClick={() => updateFilters({ acceptsInsurance: !filters.acceptsInsurance })}
            className={`
              w-full px-3 py-2 rounded-lg text-left text-sm
              flex items-center gap-2
              transition-colors duration-200
              ${filters.acceptsInsurance
                ? 'bg-sage/10 text-sage-dark font-medium'
                : 'text-navy-light hover:bg-cream'
              }
            `}
          >
            <Shield className="w-4 h-4" />
            Accepts Insurance
          </button>
        </FilterSection>
      )}
    </div>
  );
};

export default MarketplaceFilters;

